import React from 'react';
import { useTranslation } from 'react-i18next';
import { MapPin, Phone, Mail, Navigation } from 'lucide-react';

const GOOGLE_MAPS_URL =
  'https://www.google.com/maps/place/%D8%B4%D8%B1%D9%83%D8%A9+%D9%88%D8%A7%D8%B2%D9%86+%D9%84%D9%84%D9%85%D8%AD%D8%A7%D9%85%D8%A7%D8%A9+%D9%88%D8%A7%D9%84%D8%A7%D8%B3%D8%AA%D8%B4%D8%A7%D8%B1%D8%A7%D8%AA+%D8%A7%D9%84%D9%82%D8%A7%D9%86%D9%88%D9%86%D9%8A%D8%A9%E2%80%AD/@21.5117175,39.170456,16.68z/data=!4m6!3m5!1s0x15c3d1bd6bf82fd3:0x86c92165f1c8baca!8m2!3d21.5156242!4d39.168098!16s%2Fg%2F11gzm_s2s6?entry=ttu&g_ep=EgoyMDI2MDQxNS4wIKXMDSoASAFQAw%3D%3D';

const GOOGLE_MAPS_EMBED =
  'https://www.google.com/maps?q=%D8%B4%D8%B1%D9%83%D8%A9%20%D9%88%D8%A7%D8%B2%D9%86%20%D9%84%D9%84%D9%85%D8%AD%D8%A7%D9%85%D8%A7%D8%A9%20%D9%88%D8%A7%D9%84%D8%A7%D8%B3%D8%AA%D8%B4%D8%A7%D8%B1%D8%A7%D8%AA%20%D8%A7%D9%84%D9%82%D8%A7%D9%86%D9%88%D9%86%D9%8A%D8%A9%20%D8%AC%D8%AF%D8%A9&z=16&output=embed';

const LocationsSection = () => {
  const { i18n } = useTranslation();
  const isArabic = i18n.language === 'ar';

  const mainOffice = {
    nameAr: 'المقر الرئيسي – جدة',
    nameEn: 'Head Office – Jeddah',
    addressAr: 'حي الرويس، جدة، المملكة العربية السعودية',
    addressEn: 'Al Rawais District, Jeddah, Kingdom of Saudi Arabia',
    phone: '0122226665',
    email: 'info@wazin.law'
  };

  return (
    <section id="locations" className="py-24 px-4 bg-gradient-to-b from-white via-[#faf8f3] to-white relative z-10">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-14" dir={isArabic ? 'rtl' : 'ltr'}>
          <h2 className="text-4xl sm:text-5xl font-bold text-wazin-navy mb-5">
            {isArabic ? 'مقرنا الرئيسي وفروعنا' : 'Our Head Office and Branches'}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            {isArabic
              ? 'نخدمكم من مقرنا الرئيسي في جدة، مع جاهزية للتوسع وتقديم الخدمة باحترافية.'
              : 'We serve you from our head office in Jeddah, with readiness to expand and deliver our services professionally.'}
          </p>
        </div>

        <div className="grid lg:grid-cols-[1fr_1.35fr] gap-8 items-stretch" dir={isArabic ? 'rtl' : 'ltr'}>
          <div className="bg-white rounded-[30px] border border-[#A27849]/20 shadow-[0_18px_45px_rgba(15,23,42,0.08)] overflow-hidden">
            <div className="h-1.5 w-full bg-gradient-to-r from-[#B58C5A] via-[#A27849] to-[#8C653A]" />

            <div className="p-8 md:p-10">
              <div className="flex items-start gap-4 mb-8">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#B58C5A] via-[#A27849] to-[#8C653A] flex items-center justify-center shadow-md flex-shrink-0">
                  <MapPin className="w-7 h-7 text-white" />
                </div>

                <div className={isArabic ? 'text-right' : 'text-left'}>
                  <h3 className="text-2xl md:text-3xl font-bold text-wazin-navy mb-3">
                    {isArabic ? mainOffice.nameAr : mainOffice.nameEn}
                  </h3>
                  <p className="text-gray-600 text-lg leading-8">
                    {isArabic ? mainOffice.addressAr : mainOffice.addressEn}
                  </p>
                </div>
              </div>

              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-4 rounded-2xl bg-[#faf8f3] px-5 py-4">
                  <div className="w-11 h-11 rounded-xl bg-[#A27849]/12 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-[#8C653A]" />
                  </div>
                  <div className={isArabic ? 'text-right' : 'text-left'}>
                    <p className="text-sm text-gray-500 mb-1">
                      {isArabic ? 'الهاتف' : 'Phone'}
                    </p>
                    <a href={`tel:${mainOffice.phone}`} className="text-wazin-navy font-semibold hover:text-[#8C653A] transition-colors" dir="ltr">
                      {mainOffice.phone}
                    </a>
                  </div>
                </div>

                <div className="flex items-center gap-4 rounded-2xl bg-[#faf8f3] px-5 py-4">
                  <div className="w-11 h-11 rounded-xl bg-[#A27849]/12 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-[#8C653A]" />
                  </div>
                  <div className={isArabic ? 'text-right' : 'text-left'}>
                    <p className="text-sm text-gray-500 mb-1">
                      {isArabic ? 'البريد الإلكتروني' : 'Email'}
                    </p>
                    <a href={`mailto:${mainOffice.email}`} className="text-wazin-navy font-semibold hover:text-[#8C653A] transition-colors">
                      {mainOffice.email}
                    </a>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl bg-[#faf8f3] px-5 py-5 mb-8">
                <p className="text-gray-700 leading-8 text-base">
                  {isArabic
                    ? 'نخدمكم من جدة ونستعد للتوسع بما يدعم وصول خدماتنا القانونية باحترافية.'
                    : 'We serve you from Jeddah and are ready to expand in ways that support professional legal service delivery.'}
                </p>
              </div>

              <a
                href={GOOGLE_MAPS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex w-full items-center justify-center gap-3 rounded-2xl bg-gradient-to-r from-[#B58C5A] via-[#A27849] to-[#8C653A] px-6 py-4 text-lg font-bold text-white shadow-md hover:shadow-xl transition-all duration-300"
              >
                <Navigation className="w-5 h-5" />
                <span>{isArabic ? 'افتح في خرائط جوجل' : 'Open in Google Maps'}</span>
              </a>
            </div>
          </div>

          <div className="bg-white rounded-[30px] border border-[#A27849]/20 shadow-[0_18px_45px_rgba(15,23,42,0.08)] overflow-hidden">
            <div className="h-full min-h-[440px] lg:min-h-[100%]">
              <iframe
                src={GOOGLE_MAPS_EMBED}
                width="100%"
                height="100%"
                style={{ border: 0, minHeight: '440px' }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title={isArabic ? 'موقع شركة وازن على خرائط جوجل' : 'Wazin location on Google Maps'}
                className="w-full h-full"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LocationsSection;
